import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    Modal,
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    AlertTitle,
} from '@mui/material';
import {
    PhoneCallback,
    PhoneMissed,
    Schedule,
    Voicemail,
    CallEnd,
    Block,
    Sms,
    CheckCircle,
    Error,
    HourglassEmpty
} from '@mui/icons-material';
import moment from 'moment-timezone';
import './PreScreeningModal.css';

const STATUS_MAP = {
    completed: {
        label: 'Call completed successfully',
        icon: <CheckCircle color="success" />,
        color: '#4caf50'
    },
    busy: {
        label: 'Candidate was busy',
        icon: <Schedule color="warning" />,
        color: '#ff9800'
    },
    silent: {
        label: 'No response during call',
        icon: <PhoneMissed color="error" />,
        color: '#f44336'
    },
    voicemail: {
        label: 'Call reached voicemail',
        icon: <Voicemail color="primary" />,
        color: '#2196f3'
    },
    hungup: {
        label: 'Candidate hung up during call',
        icon: <CallEnd color="error" />,
        color: '#f44336'
    },
    unreachable: {
        label: 'Phone number unreachable',
        icon: <Block color="error" />,
        color: '#f44336'
    },
    missed: {
        label: 'Call was missed (no answer, no voicemail)',
        icon: <PhoneMissed color="error" />,
        color: '#f44336'
    },
    retry_pending: {
        label: 'Call failed, retry pending',
        icon: <HourglassEmpty color="warning" />,
        color: '#fff1b8'
    },
    // Original failure statuses (red) - when reached max retry
    international_blocked: {
        label: 'International calling blocked',
        icon: <Block color="error" />,
        color: '#f44336'
    },
    twilio_error: {
        label: 'Twilio service error',
        icon: <Error color="error" />,
        color: '#f44336'
    },
    call_failed: {
        label: 'Call failed',
        icon: <Error color="error" />,
        color: '#f44336'
    },
    pending: {
        label: 'Prescreening in progress',
        icon: <HourglassEmpty color="primary" />,
        color: '#2196f3'
    },
    unknown: {
        label: 'Unknown status',
        icon: <Error color="action" />,
        color: '#757575'
    }
};

// Modal style defined outside component to prevent recreation
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    borderRadius: '8px',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
    p: '30px',
    overflowY: 'auto',
    overflowX: 'hidden',
};

// Format date utility function
const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    const date = moment(dateString);
    return date.isValid()
        ? date.format('DD/MM/YYYY hh:mm A')
        : dateString;
};

function PrescreeningStatusModal({ open, setOpen, record, backendURL, getCookie, onRetryPrescreen }) {
    const [loading, setLoading] = useState(true);
    const [statusData, setStatusData] = useState(null);
    const [logData, setLogData] = useState([]);
    const [error, setError] = useState(null);

    // Memoize the fetch function to prevent recreating it on each render
    const fetchPrescreeningStatus = useCallback(async () => {
        if (!record?.ApplicationID) return;

        setLoading(true);
        setError(null);

        try {
            // Get current prescreening status
            const statusUrl = `${backendURL}/v1/prescreen-status?sessionID=${getCookie("userSession")}&applicationID=${record.ApplicationID}`;
            const statusResponse = await axios.get(statusUrl);

            // Process numeric values once when receiving data
            const processedData = {
                ...statusResponse.data,
                CurrentRetryCounts: Number(statusResponse.data.CurrentRetryCounts || 0),
                MaxRetryCounts: Number(statusResponse.data.MaxRetryCounts || 0),
            };
            setStatusData(processedData);

            // Get prescreening log history
            const logUrl = `${backendURL}/v1/prescreen-log?sessionID=${getCookie("userSession")}&applicationID=${record.ApplicationID}`;
            const logResponse = await axios.get(logUrl);
            setLogData(logResponse.data.logs || []);
        } catch (err) {
            setError('Failed to load prescreening status information');
        } finally {
            setLoading(false);
        }
    }, [backendURL, getCookie, record?.ApplicationID]);

    // Clean up function for when modal closes
    const handleClose = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    // Handler for retry button
    const handleRetry = useCallback(() => {
        if (onRetryPrescreen && record) {
            onRetryPrescreen(record);
        }
        handleClose();
    }, [onRetryPrescreen, record, handleClose]);



    // Effect to fetch data when modal opens
    useEffect(() => {
        if (open && record) {
            fetchPrescreeningStatus();
        }

        // Reset states when modal closes
        return () => {
            if (!open) {
                setStatusData(null);
                setLogData([]);
                setError(null);
            }
        };
    }, [open, record, fetchPrescreeningStatus]);

    // Memoize sorted log data to prevent recalculation on every render
    const sortedLogs = useMemo(() => {
        if (!logData.length) return [];

        return [...logData].sort((a, b) => {
            const dateA = new Date(a.Timestamp);
            const dateB = new Date(b.Timestamp);
            return dateB - dateA; // Newest first
        });
    }, [logData]);

    // Memoize current status to avoid recalculating on every render
    const currentStatus = useMemo(() => {
        if (!sortedLogs.length) return 'pending';

        const lastLogEntry = sortedLogs[0];
        const lastStatus = lastLogEntry.Status || lastLogEntry.status;

        return lastStatus && lastStatus !== 'pending' ? lastStatus : 'pending';
    }, [sortedLogs]);

    // Early return if modal is closed
    if (!open) return null;

    // Get the status info
    const statusInfo = STATUS_MAP[currentStatus] || STATUS_MAP.unknown;
    const maxReached = statusData && statusData.CurrentRetryCounts >= statusData.MaxRetryCounts+1;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            destroyonclose={true}
            aria-labelledby="prescreening-status-modal-title"
            BackdropProps={{
                style: {
                    backdropFilter: 'blur(3px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                },
            }}
        >
            <Box sx={modalStyle}>
                <Typography
                    id="prescreening-status-modal-title"
                    variant="h5"
                    component="h2"
                    sx={{ mb: 3, color: '#303f9f', fontWeight: 600 }}
                >
                    Prescreening Status: {record?.Name}
                </Typography>

                {statusData && maxReached && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <AlertTitle>Maximum retry attempts reached</AlertTitle>
                        <Typography variant="body2">
                            The system has made {statusData.CurrentRetryCounts} call attempts, which is the maximum configured
                            number of retries.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            No more automatic calls will be made. You may manually retry the prescreening process if needed.
                        </Typography>
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                ) : (
                    <>
                        {statusData && (
                            <Alert
                                severity={currentStatus === 'completed' ? 'success' : maxReached ? 'warning' : 'info'}
                                icon={statusInfo.icon}
                                sx={{ mb: 3 }}
                            >
                                <AlertTitle>Last status: {statusInfo.label}</AlertTitle>
                                <Typography variant="body2">
                                    Call attempts: {statusData.CurrentRetryCounts} of {statusData.MaxRetryCounts+1}
                                    {maxReached && ' (Maximum reached)'}
                                </Typography>
                                {statusData.LastRetrytime && (
                                    <Typography variant="body2">
                                        Last attempted: {formatDate(statusData.LastRetrytime)}
                                    </Typography>
                                )}
                            </Alert>
                        )}

                        {/* Call History section */}
                        <Typography variant="h6" component="h3" sx={{ mt: 4, mb: 2 }}>
                            Call History
                        </Typography>

                        {sortedLogs.length > 0 ? (
                            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                            <TableCell sx={{ fontWeight: 600 }}>Attempt</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Final SMS Sent</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedLogs.map((log, index) => {
                                            const statusInfo = STATUS_MAP[log.Status] || STATUS_MAP.unknown;
                                            const attemptNumber = sortedLogs.length - index;

                                            return (
                                                <TableRow key={index}>
                                                    <TableCell>{attemptNumber}</TableCell>
                                                    <TableCell>{formatDate(log.Timestamp)}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            {statusInfo.icon}
                                                            <Typography sx={{ ml: 1 }}>
                                                                {statusInfo.label}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {log.SMSSent ? (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', color: '#4caf50' }}>
                                                                <Sms fontSize="small" sx={{ mr: 1 }} />
                                                                <Typography>Yes</Typography>
                                                            </Box>
                                                        ) : (
                                                            <Typography color="text.secondary">No</Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Alert severity="info" sx={{ mb: 3 }}>
                                {statusData && statusData.CurrentRetryCounts > 0 ?
                                    "Call log information is unavailable, but calls have been attempted." :
                                    "No call attempts have been made yet. The system will begin calling soon."
                                }
                            </Alert>
                        )}


                    </>
                )}

                <Box display="flex" justifyContent="flex-end" gap={2}>
                    {statusData && maxReached && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<PhoneCallback />}
                            onClick={handleRetry}
                        >
                            Retry Prescreening
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}

export default React.memo(PrescreeningStatusModal);